/**
 * Boots the app bar HUD, on its own.
 *
 * A second entry module on `/app`, deliberately independent of
 * `src/app/main.ts`. Two reasons, and the second is the real one:
 *
 *   1. **Lane separation.** The editor shell is another fork's file. A
 *      self-booting module means wiring identity costs them a one-line
 *      `data-*` attribute in the generator and nothing else.
 *   2. **The account control should outlive an editor crash.** `main.ts`
 *      already catches startup failure and shows a fallback — but if identity
 *      mounted from inside that boot, a user whose editor failed to start would
 *      also lose the way to reach settings, billing, or sign-out. Those are
 *      exactly the things someone reaches for when the app is broken.
 *
 * The editor drives the save indicator through a DOM event rather than an
 * import, so neither side needs a reference to the other. See
 * `docs/decisions/auth-shell-contract.md`.
 */

import { announceChange, refresh } from "../auth/session";
import { signOut } from "../auth/sign-in";
import { type AppBarHud, mountAppBarHud } from "./index";
import type { SaveState } from "./save-state";

/** The event the editor fires to move the save indicator. */
export const SAVE_STATE_EVENT = "rcm:save-state";

declare global {
  interface DocumentEventMap {
    [SAVE_STATE_EVENT]: CustomEvent<SaveState>;
  }
}

/**
 * The mounted instance, if there is one.
 *
 * Booting is idempotent for two reasons, and the second is a bug I shipped in
 * the same commit that documented it:
 *
 *   1. Vite re-executes a module on hot update, so in development every edit
 *      would have added a second account menu and a second set of document
 *      listeners.
 *   2. `auth-shell-contract.md` tells the shell to
 *      `import { setSaveState } from "../hud/app-bar"` — and a bare import of
 *      this module runs the boot at the bottom as a side effect. So the shell
 *      following its own contract would have mounted the HUD a second time,
 *      before the page's own script tag got to it.
 */
let mounted: AppBarHud | null = null;

export function bootAppBar(root: ParentNode = document): AppBarHud | null {
  if (mounted) return mounted;

  const save = root.querySelector<HTMLElement>("[data-save-slot]");
  const account = root.querySelector<HTMLElement>("[data-account-slot]");
  if (!save && !account) return null;

  const hud = mountAppBarHud(
    { save, account },
    {
      onSignOut: async () => {
        await signOut();
        // Tell the other tabs before re-reading, so a second window does not
        // sit on a session that has already ended.
        announceChange();
        await refresh();
      },
    },
  );

  // The editor owns save state; the HUD owns how it looks. An event keeps that
  // boundary without either module importing the other.
  const onSaveState = (event: Event) => {
    hud.save?.set((event as CustomEvent<SaveState>).detail);
  };
  document.addEventListener(SAVE_STATE_EVENT, onSaveState);

  // One read on load. Without it the bar renders from SIGNED_OUT and flips a
  // moment later, which reads as a glitch rather than as loading.
  void refresh();

  mounted = {
    ...hud,
    destroy() {
      // The listener is on `document` and outlives the components, so tearing
      // down without removing it leaves a handler holding a dead indicator.
      document.removeEventListener(SAVE_STATE_EVENT, onSaveState);
      hud.destroy();
      mounted = null;
    },
  };
  return mounted;
}

/** Tear down and allow a re-boot. Used by the dev hot-update hook below. */
export function unmountAppBar() {
  mounted?.destroy();
}

/**
 * Fire-and-forget helper for the editor.
 *
 * Exported so the shell can import one function rather than construct the
 * event, but it is only a `CustomEvent` — anything can drive this, including
 * code that cannot import from `src/hud`.
 */
export function setSaveState(state: SaveState) {
  document.dispatchEvent(new CustomEvent(SAVE_STATE_EVENT, { detail: state }));
}

bootAppBar();

// Vite replaces this module on edit; without disposing first, each hot update
// would leave the previous menu in the DOM beside the new one.
if (import.meta.hot) import.meta.hot.dispose(() => unmountAppBar());
